// Weekly Relationship Challenges for Relationship Compass
// Evidence-based practices aligned with the 8 modules
// Australian English throughout

const relationshipChallenges = [
  {
    id: 1,
    title: "The 5:1 Ratio Week",
    description: "Track your positive-to-negative interaction ratio daily for one week. Aim for Gottman's 5:1 rule of thumb for positive to negative interactions.",
    category: "Appreciation & Connection",
    difficulty: "Beginner",
    timeCommitment: "10 mins/day for 7 days",
    why: "Gottman found that couples who stayed together showed about 5 positive interactions for every 1 negative when they disagreed. This challenge builds awareness and shifts the ratio.",
    points: 15,
    module: 1
  },
  {
    id: 2,
    title: "Seven-Day Appreciation Practice",
    description: "Express one specific appreciation daily using the three-part formula: behaviour + impact + value. Track your partner's response and your consistency.",
    category: "Appreciation & Connection",
    difficulty: "Beginner",
    timeCommitment: "5 mins/day for 7 days",
    why: "Generic praise ('You're great!') doesn't build emotional bank accounts. Specific appreciation lands powerfully and creates lasting connection.",
    points: 10,
    module: 1
  },
  {
    id: 3,
    title: "Receiving Practice Week",
    description: "When your partner appreciates you this week, practise receiving genuinely: 'Thank you, that means a lot.' No deflecting, minimising, or 'It was nothing.'",
    category: "Appreciation & Connection",
    difficulty: "Intermediate",
    timeCommitment: "Ongoing awareness for 7 days",
    why: "Deflecting appreciation breaks the connection loop. Learning to receive gracefully completes the circuit and strengthens bonds.",
    points: 15,
    module: 1
  },
  {
    id: 4,
    title: "Emotional Precision Challenge",
    description: "Go beyond 'fine,' 'good,' 'stressed' this week. Use the Feeling Wheel to name specific emotions: disappointed, overwhelmed, hopeful, anxious, grateful.",
    category: "Emotional Intelligence",
    difficulty: "Beginner",
    timeCommitment: "Ongoing awareness for 7 days",
    why: "Most of us reach for the same few emotion words. The Feeling Wheel has over 130. Precision helps your partner understand your inner world accurately.",
    points: 10,
    module: 2
  },
  {
    id: 5,
    title: "Validation Without Agreement",
    description: "Practise validating your partner's perspective this week without necessarily agreeing: 'That makes sense' or 'I can see why you'd feel that way.'",
    category: "Emotional Intelligence",
    difficulty: "Intermediate",
    timeCommitment: "Ongoing practice for 7 days",
    why: "Validation isn't surrender; it's understanding. You can see their perspective whilst having your own. This builds emotional safety.",
    points: 20,
    module: 2
  },
  {
    id: 6,
    title: "Attunement Practice Week",
    description: "Notice and respond to your partner's emotional state daily, even when they haven't said anything. 'You seem quiet, everything okay?' Track your accuracy.",
    category: "Emotional Intelligence",
    difficulty: "Advanced",
    timeCommitment: "10 mins/day for 7 days",
    why: "Attunement is sensing shifts in energy, tone, body language and checking in. It's how partners feel truly seen without having to explicitly ask.",
    points: 25,
    module: 2
  },
  {
    id: 7,
    title: "Four Horsemen Detector",
    description: "Track when you use criticism, contempt, defensiveness, or stonewalling this week. Replace each horseman with its antidote immediately.",
    category: "Communication",
    difficulty: "Intermediate",
    timeCommitment: "Ongoing awareness for 7 days",
    why: "The Four Horsemen were among the strongest predictors of divorce in Gottman's research. Becoming aware of these patterns is the first step to replacing them with healthier communication.",
    points: 20,
    module: 3
  },
  {
    id: 8,
    title: "Active Listening (LAER) Practice",
    description: "In 5 conversations this week, use LAER method: Listen fully, Acknowledge what you heard, Explore for understanding, then Respond. No interrupting.",
    category: "Communication",
    difficulty: "Intermediate",
    timeCommitment: "5 conversations (15 mins each)",
    why: "It's easy to listen to reply rather than to understand. LAER builds genuine understanding before you respond, which helps prevent misunderstandings.",
    points: 15,
    module: 3
  },
  {
    id: 9,
    title: "Softened Startup Week",
    description: "Every difficult conversation this week, start gently: 'I feel [emotion] about [situation]. I need [request]' instead of 'You always...' or 'Why don't you ever...'",
    category: "Communication",
    difficulty: "Intermediate",
    timeCommitment: "Ongoing practice for 7 days",
    why: "Gottman reports that how a conflict conversation starts predicts how it ends 96% of the time. Softened startup reduces defensive reactions and creates space for productive dialogue.",
    points: 15,
    module: 3
  },
  {
    id: 10,
    title: "Solvable vs Perpetual Identification",
    description: "List your recurring conflicts. Which are solvable (situational) vs perpetual (value/personality differences)? Stop trying to 'solve' perpetual problems.",
    category: "Conflict Resolution",
    difficulty: "Advanced",
    timeCommitment: "30 mins + ongoing awareness",
    why: "Gottman estimates that 69% of conflicts are perpetual. Fighting the wrong battle creates frustration. Perpetual problems need dialogue and compromise, not solutions.",
    points: 25,
    module: 4
  },
  {
    id: 11,
    title: "Flooding Break Practice",
    description: "When you feel flooded this week (racing heart, tight chest, tunnel vision), call timeout: 'I need 20 minutes.' Then return to discuss.",
    category: "Conflict Resolution",
    difficulty: "Intermediate",
    timeCommitment: "As needed during conflict",
    why: "Gottman treats a heart rate above about 100bpm as a sign of flooding, when calm conversation becomes very hard. Breaks prevent damage, but you must return to the conversation after calming.",
    points: 20,
    module: 4
  },
  {
    id: 12,
    title: "Repair Attempt Challenge",
    description: "When conflict escalates this week, try repair attempts: 'That came out wrong,' 'Can we start over?' or humour. Notice if partner accepts the repair.",
    category: "Conflict Resolution",
    difficulty: "Intermediate",
    timeCommitment: "During conflicts for 7 days",
    why: "Repair attempts de-escalate negativity spirals. Successful couples make and accept repairs frequently. Failing couples miss or reject them.",
    points: 15,
    module: 4
  },
  {
    id: 13,
    title: "Five Dimensions Check-In",
    description: "Rate all five intimacy dimensions (emotional, physical, intellectual, experiential, spiritual) 1-10. Discuss which need attention and make a plan.",
    category: "Intimacy & Vulnerability",
    difficulty: "Intermediate",
    timeCommitment: "45 mins",
    why: "Many couples maintain physical connection whilst other dimensions drift. Holistic intimacy requires nurturing all five areas intentionally.",
    points: 20,
    module: 5
  },
  {
    id: 14,
    title: "Calibrated Vulnerability Week",
    description: "Share something vulnerable daily (incrementally deeper). Watch for reciprocity. Notice if vulnerability is building closeness or creating distance.",
    category: "Intimacy & Vulnerability",
    difficulty: "Advanced",
    timeCommitment: "10 mins/day for 7 days",
    why: "Vulnerability builds intimacy when it's paced: share a little at a time, watch for reciprocity, and share with someone who has earned it. This practice builds that skill.",
    points: 25,
    module: 5
  },
  {
    id: 15,
    title: "Desire Conversation Challenge",
    description: "Have a non-bedroom conversation about desire this week: 'What helps you feel desire? What creates pressure? How can we honour both our needs?'",
    category: "Intimacy & Vulnerability",
    difficulty: "Advanced",
    timeCommitment: "45 mins",
    why: "Desire discrepancy is normal. It becomes problematic when unspoken. Talking about it outside the bedroom reduces pressure and creates understanding.",
    points: 25,
    module: 5
  },
  {
    id: 16,
    title: "Small Promises Week",
    description: "Keep every small promise you make this week: texts, times, tasks. Trust is built through boring consistency, not grand gestures.",
    category: "Trust",
    difficulty: "Beginner",
    timeCommitment: "Ongoing awareness for 7 days",
    why: "Trust erodes through accumulated small broken promises. One forgotten commitment is forgivable; thirty destroy the foundation.",
    points: 10,
    module: 6
  },
  {
    id: 17,
    title: "Trust Component Assessment",
    description: "Rate your relationship on the five trust components from Module 6: transparency, reliability, attunement, non-defensiveness, selective sharing. Discuss findings.",
    category: "Trust",
    difficulty: "Intermediate",
    timeCommitment: "30 mins",
    why: "Trust isn't vague. It has specific components you can assess and build. Knowing which areas need work allows intentional strengthening.",
    points: 15,
    module: 6
  },
  {
    id: 18,
    title: "Trust Agreement Creation",
    description: "Build 3-5 explicit trust agreements this week: communication expectations, boundaries, finances. Make implicit expectations explicit.",
    category: "Trust",
    difficulty: "Advanced",
    timeCommitment: "60 mins",
    why: "Many trust breaches happen when partners have different unspoken expectations. Explicit, freely agreed expectations help prevent accidental violations. (Agreements should be mutual and revisable; one-sided rules about phones, money or friends are a sign of control, not trust.)",
    points: 25,
    module: 6
  },
  {
    id: 19,
    title: "Individual Growth Support",
    description: "Identify one of your partner's individual goals. This week, provide all four types of support: permission, encouragement, practical help, emotional presence.",
    category: "Growing Together",
    difficulty: "Intermediate",
    timeCommitment: "Ongoing throughout week",
    why: "Healthy relationships balance togetherness with individual growth. Supporting your partner's development enriches the relationship, doesn't threaten it.",
    points: 20,
    module: 7
  },
  {
    id: 20,
    title: "Shared Vision Conversation",
    description: "Discuss your vision for 5 years from now across 4 areas: career, family, lifestyle, finances. Identify alignment and misalignment.",
    category: "Growing Together",
    difficulty: "Advanced",
    timeCommitment: "60-90 mins",
    why: "Couples aligned on major life questions navigate challenges better. Unspoken assumptions about the future create conflict when they surface later.",
    points: 30,
    module: 7
  },
  {
    id: 21,
    title: "Turning Toward Bids Challenge",
    description: "Track how often you turn toward (engage) vs turn away (ignore) your partner's bids for connection this week. Aim to turn towards most of them.",
    category: "Resilience",
    difficulty: "Intermediate",
    timeCommitment: "Ongoing awareness for 7 days",
    why: "In Gottman's six-year study of newlyweds, couples who stayed married had turned towards bids about 86% of the time, versus about 33% for couples who divorced. These small moments of connection are relationship glue.",
    points: 20,
    module: 8
  },
  {
    id: 22,
    title: "Daily Ritual Establishment",
    description: "Choose one daily connection ritual this week and do it consistently: 6-second kiss goodbye, evening check-in, or bedtime appreciation.",
    category: "Resilience",
    difficulty: "Beginner",
    timeCommitment: "5-10 mins/day",
    why: "Small consistent practices beat occasional grand gestures. Daily rituals maintain connection through life's busyness.",
    points: 15,
    module: 8
  },
  {
    id: 23,
    title: "Monthly Temperature Check",
    description: "Rate your relationship across all 8 dimensions (appreciation, emotions, communication, conflict, intimacy, trust, growth, resilience). Discuss findings.",
    category: "Resilience",
    difficulty: "Intermediate",
    timeCommitment: "45 mins",
    why: "Regular temperature checks catch problems before crises. What gets measured gets managed, including relationship health.",
    points: 20,
    module: 8
  },
  {
    id: 24,
    title: "Gratitude Journal Week",
    description: "Write down 3 things you appreciate about your partner daily for 7 days. Share them at week's end. Notice shifts in your perspective.",
    category: "Appreciation & Connection",
    difficulty: "Beginner",
    timeCommitment: "5 mins/day + 15 mins sharing",
    why: "What you focus on grows. Gratitude practice shifts you into positive sentiment override, where you see your partner through appreciative lens.",
    points: 10,
    module: 1
  },
  {
    id: 25,
    title: "No Phones Week",
    description: "During meals and evening wind-down, put phones completely away for entire week. Be fully present with your partner instead.",
    category: "Appreciation & Connection",
    difficulty: "Intermediate",
    timeCommitment: "Ongoing for 7 days",
    why: "Phubbing (phone snubbing) can quietly damage connection. Full presence, even briefly, builds intimacy more than distracted hours together.",
    points: 15,
    module: 1
  },
  {
    id: 26,
    title: "Positive Sentiment Override Practice",
    description: "When your partner does something ambiguous this week, consciously choose positive interpretation: 'They're stressed' not 'They don't care.'",
    category: "Resilience",
    difficulty: "Intermediate",
    timeCommitment: "Ongoing awareness for 7 days",
    why: "Positive sentiment override (benefit of the doubt) vs negative (assuming the worst) colours how you experience everything your partner does. You can train this mindset.",
    points: 15,
    module: 8
  }
];

// Function to get challenge of the week (deterministic based on week number)
export const getChallengeOfTheWeek = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  const weekNumber = Math.ceil((((now - start) / 86400000) + start.getDay() + 1) / 7);
  const challengeIndex = weekNumber % relationshipChallenges.length;
  return relationshipChallenges[challengeIndex];
};

export default relationshipChallenges;
